import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Navigation, List, Map as MapIcon, Droplet, Zap, Heart, Shield, Bus, Flag, Building2 } from "lucide-react";
import api from "@/lib/api";
import { Page, Spinner } from "@/components/shared";

const TYPE_ICON = { medical: Heart, hq: Building2, transit: Bus, meeting: Flag, security: Shield, water: Droplet };
const TYPE_COLOR = { medical: "var(--cs-red)", hq: "var(--cs-magenta)", transit: "var(--cs-blue)", meeting: "var(--cs-green)", security: "var(--cs-blue)", water: "var(--cs-blue)" };
const SERVICE_ICON = { Water: Droplet, Charging: Zap, "First Aid": Heart, Medical: Heart, Security: Shield, Transit: Bus };

export default function SafePoints() {
  const nav = useNavigate();
  const [pts, setPts] = useState(null);
  const [view, setView] = useState("list");

  useEffect(() => {
    api.get("/safe-points").then(({ data }) => {
      setPts(data);
      localStorage.setItem("cs_cache_safepoints", JSON.stringify(data));
    }).catch(() => {
      const c = localStorage.getItem("cs_cache_safepoints");
      setPts(c ? JSON.parse(c) : []);
    });
  }, []);

  if (!pts) return <div className="px-6 pt-10"><Spinner /></div>;

  return (
    <Page className="px-5 pt-6">
      <button data-testid="safepoints-back" onClick={() => nav("/app")} className="cs-tap flex items-center gap-1 text-sm mb-4" style={{ color: "var(--cs-fg2)" }}>
        <ArrowLeft size={18} /> Home
      </button>
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-3xl">Safe Points</h1>
        <div className="flex rounded-full p-1" style={{ background: "var(--cs-card)" }}>
          <button data-testid="safepoints-view-map" onClick={() => setView("map")} className="cs-tap rounded-full p-2" style={{ background: view === "map" ? "var(--cs-magenta)" : "transparent" }}>
            <MapIcon size={16} className="text-white" />
          </button>
          <button data-testid="safepoints-view-list" onClick={() => setView("list")} className="cs-tap rounded-full p-2" style={{ background: view === "list" ? "var(--cs-magenta)" : "transparent" }}>
            <List size={16} className="text-white" />
          </button>
        </div>
      </div>

      {view === "map" ? (
        <div className="relative mt-4 rounded-2xl overflow-hidden" style={{ height: 340, background: "linear-gradient(135deg,#12121a,#1a1a24)", border: "1px solid var(--cs-border)" }}>
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "linear-gradient(var(--cs-border) 1px, transparent 1px), linear-gradient(90deg, var(--cs-border) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
          {pts.slice(0, 6).map((p, i) => {
            const Icon = TYPE_ICON[p.type] || MapPin;
            const left = 15 + (i % 3) * 30 + (i * 7) % 12;
            const top = 18 + Math.floor(i / 3) * 34 + (i * 5) % 10;
            return (
              <div key={p.id} data-testid={`safepoints-map-pin-${p.id}`} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${left}%`, top: `${top}%` }}>
                <span className="grid place-items-center rounded-full magenta-glow" style={{ width: 34, height: 34, background: TYPE_COLOR[p.type] || "var(--cs-magenta)" }}>
                  <Icon size={16} className="text-white" />
                </span>
                <span className="text-[9px] mt-1 px-1 rounded" style={{ background: "rgba(0,0,0,0.6)" }}>{p.distance_km}km</span>
              </div>
            );
          })}
          <div className="absolute bottom-3 left-3 text-[10px] px-2 py-1 rounded" style={{ background: "rgba(0,0,0,0.6)", color: "var(--cs-fg2)" }}>
            Demo map · connect a provider later
          </div>
        </div>
      ) : null}

      <div className="mt-4 space-y-3">
        {pts.map((p) => {
          const Icon = TYPE_ICON[p.type] || MapPin;
          return (
            <div key={p.id} data-testid={`safe-point-card-${p.id}`} className="cs-card p-4">
              <div className="flex items-start justify-between">
                <div className="flex gap-3">
                  <span className="grid place-items-center rounded-xl flex-shrink-0" style={{ width: 44, height: 44, background: `${TYPE_COLOR[p.type] || "var(--cs-magenta)"}22` }}>
                    <Icon size={22} style={{ color: TYPE_COLOR[p.type] || "var(--cs-magenta)" }} />
                  </span>
                  <div>
                    <p className="font-head font-bold text-base leading-tight">{p.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--cs-fg2)" }}>{p.hours}</p>
                  </div>
                </div>
                <span className="text-sm font-bold whitespace-nowrap" style={{ color: "var(--cs-green)" }}>{p.distance_km} km</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {p.services.map((s) => (
                  <span key={s} className="text-[11px] rounded-full px-2.5 py-1" style={{ background: "var(--cs-card-hi)", color: "var(--cs-fg2)" }}>{s}</span>
                ))}
              </div>
              <a data-testid={`safe-point-directions-${p.id}`} href={`https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`} target="_blank" rel="noreferrer"
                className="cs-tap flex items-center justify-center gap-2 mt-3 rounded-xl font-semibold text-sm no-underline" style={{ height: 46, background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)", color: "#fff" }}>
                <Navigation size={16} /> Get Directions
              </a>
            </div>
          );
        })}
      </div>
    </Page>
  );
}
